import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import './StudentPerformance.css';
import { useAuth } from "../../AuthContext";
import configData from '../../configure';

const StudentPerformanceDashboard = ({ selectedUser }) => {
  const [performanceData, setPerformanceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState(null);
  const [courseNameMap, setCourseNameMap] = useState({});
  const [eventNameMap, setEventNameMap] = useState({});
  const { axios, orgId, token } = useAuth();
  const userId = selectedUser?.studentInfo?.userId || selectedUser?.userId;
  const [selectedYear, setSelectedYear] = useState('2025-2026');
  const academicYears = ['All Years', '2024-2025', '2025-2026', '2026-2027', '2027-2028', '2028-2029', '2029-2030'];

  useEffect(() => {
    if (!selectedUser) return;
    
    setLoading(true);
    let url = `${configData.STUDENT_PERFORMANCE}/user/${userId}?pageSize=20`;
    if (selectedYear !== 'All Years') {
      url += `&academicYear=${selectedYear}`;
    }
    
    axios
      .get(url)
      .then((response) => {
        const items = response.data.items || [];
        setPerformanceData(items);
        if (items.length > 0) {
          setActiveTab(items[0].studentPerformanceId);
        } else {
          setActiveTab(null);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching student performance data:", error);
        toast.error("Failed to load student performance data");
        setLoading(false);
        setPerformanceData([]);
      });
  }, [selectedUser, axios, selectedYear, userId]);

  // Fetch human-readable course names for all GUIDs in the performance data
  useEffect(() => {
    if (!performanceData || !performanceData.length) return;
    
    const uniqueCourseIds = new Set();
    performanceData.forEach(assessment => {
      assessment?.assessmentPerformanceList?.forEach(course => {
        if (course?.courseId) uniqueCourseIds.add(course.courseId);
      });
    });

    uniqueCourseIds.forEach(async (id) => {
      if (courseNameMap[id]) return; // Already fetched
      try {
        const response = await axios.get(configData.get_resources_resources_courses(id));
        const name = response.data?.courseDetails?.courseName || 'Unknown Subject';
        setCourseNameMap(prev => ({ ...prev, [id]: name }));
      } catch (err) {
        console.error(`Failed to fetch course name for ${id}:`, err);
        setCourseNameMap(prev => ({ ...prev, [id]: id })); // Fallback to ID
      }
    });
  }, [performanceData, axios, courseNameMap]);

  // Fetch human-readable event/exam names for individual assessments
  useEffect(() => {
    if (!performanceData || !performanceData.length || !orgId || !token) return;

    const fetchEventNames = async () => {
      // 1. Target unique IDs from the performance records
      const idsToResolve = new Set();
      const perfIdMap = new Map(); // Map eventId -> firstAssessmentId

      performanceData.forEach(item => {
        const eid = item.eventId || item.assessmentId;
        const aid = item.assessmentPerformanceList?.[0]?.assessmentId;
        if (eid) {
          idsToResolve.add(eid);
          if (aid) perfIdMap.set(eid, aid);
        }
      });

      for (const id of idsToResolve) {
        if (eventNameMap[id]) continue; 

        try {
          // STRATEGY A: Direct Event ID Lookup
          // Bypasses filtered list restrictions
          const eventRes = await axios.get(`${configData.RESOURCES_EVENTS.replace('?', '')}/${id}`, {
            headers: { Authorization: token }
          }).catch(() => null);
          
          if (eventRes?.data?.eventName) {
            setEventNameMap(prev => ({ ...prev, [id]: eventRes.data.eventName }));
            continue;
          }

          // STRATEGY B: Assessment Metadata Extraction
          // Get the name of the first assessment in this performance record
          const aid = perfIdMap.get(id);
          if (aid) {
            const assBase = configData.RESOURCES_ASSESSMENT_EVENT.split('/event')[0];
            const assRes = await axios.get(`${assBase}/${aid}`, {
              headers: { Authorization: token }
            }).catch(() => null);

            if (assRes?.data?.assessmentName) {
              setEventNameMap(prev => ({ ...prev, [id]: assRes.data.assessmentName }));
              continue;
            }
          }

          // STRATEGY C: Filtered Collection Search (Fallback)
          const searchRes = await axios.get(configData.RESOURCES_EVENTS, {
            params: { orgId, eventId: id, pageSize: 1, eventStartDate: '2024-01-01T00:00:00Z', eventEndDate: '2034-12-31T23:59:59Z' },
            headers: { Authorization: token }
          }).catch(() => null);
          
          if (searchRes?.data?.items?.[0]?.eventName) {
            setEventNameMap(prev => ({ ...prev, [id]: searchRes.data.items[0].eventName }));
            continue;
          }

          // Marked as resolved but unknown
          setEventNameMap(prev => ({ ...prev, [id]: null }));
        } catch (err) {
          setEventNameMap(prev => ({ ...prev, [id]: null }));
        }
      }
    };

    fetchEventNames();
  }, [performanceData, orgId, token, axios, eventNameMap]);

  /* 
  const handleEdit = (assessment) => {
    setEditMode(true);
    setEditData({
      ...assessment,
      assessmentPerformanceList: [...assessment.assessmentPerformanceList]
    });
  };
  */

  const handleInputChange = (index, field, value) => {
    const updatedPerformance = [...editData.assessmentPerformanceList];
    const item = updatedPerformance[index];

    if (field === 'marksObtained') {
      const obtained = parseInt(value);
      const max = parseInt(item.maxMarks) || 100;
      if (!isNaN(obtained) && obtained > max) {
        // Warning already provided by browser if usando 'max' attribute, 
        // but we enforce it here for better UX
        return; 
      }
      
      // Update grade on the fly
      // const grade = getGradeClass(obtained, max); 
      // Note: the getGradeClass in this file currently only returns a CSS class name.
      // We might want to update the actual grade value if there is a mapping.
    }

    updatedPerformance[index] = {
      ...item,
      [field]: value
    };

    setEditData({
      ...editData,
      assessmentPerformanceList: updatedPerformance
    });
  };

  const handleRemarksChange = (value) => {
    setEditData({
      ...editData,
      remarks: value
    });
  };

  const handleSave = async () => {
    try {
      await axios.put(
        `${configData.STUDENT_PERFORMANCE}/${editData.studentPerformanceId}`,
        editData
      );
      setPerformanceData(
        performanceData.map((item) =>
          item.studentPerformanceId === editData.studentPerformanceId ? editData : item
        )
      );
      setEditMode(false);
      setEditData(null);
      toast.success("Student performance updated successfully!");
    } catch (error) {
      console.error("Error updating student performance:", error);
      toast.error("Failed to update student performance data");
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setEditData(null);
  };

  const calculateOverallAverage = () => {
    if (!performanceData || !performanceData.length) return 0;
    
    let totalMarks = 0;
    let totalCourses = 0;
    
    performanceData.forEach(assessment => {
      assessment?.assessmentPerformanceList?.forEach(course => {
        totalMarks += parseInt(course?.marksObtained || 0);
        totalCourses++;
      });
    });
    
    return totalCourses > 0 ? (totalMarks / totalCourses).toFixed(1) : 0;
  };

  const getGradeClass = (grade) => {
    if (!grade) return 'sp-grade-f';
    switch(grade[0]) {
      case 'A': return 'sp-grade-a';
      case 'B': return 'sp-grade-b';
      case 'C': return 'sp-grade-c';
      case 'D': return 'sp-grade-d';
      default: return 'sp-grade-f';
    }
  };

  return (
    <div className="sp-student-dashboard">
      <div className="sp-header-flex">
        <h1 className="sp-dashboard-title">Student Performance</h1>
        <div className="sp-filter-group">
          <label className="sp-filter-label">Academic Year</label>
          <select 
            className="sp-year-select"
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            {academicYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="sp-loading-message">Loading performance data for {selectedYear}...</div>
      ) : performanceData.length === 0 ? (
        <div className="sp-no-data-message">
          No performance data available for {selectedYear}.
          <br />
          <p style={{fontSize: '14px', color: '#666', marginTop: '10px'}}>Try selecting a different academic year or "All Years".</p>
        </div>
      ) : (
        <>
          <div className="sp-stats-container">
        <div className="sp-stat-card">
          <div className="sp-stat-content">
            <p className="sp-stat-label">Overall Average</p>
            <h3 className="sp-stat-value">{calculateOverallAverage()}%</h3>
          </div>
        </div>
        
        <div className="sp-stat-card">
          <div className="sp-stat-content">
            <p className="sp-stat-label">Total Exams</p>
            <h3 className="sp-stat-value">{performanceData.length}</h3>
          </div>
        </div>
        
        <div className="sp-stat-card">
          <div className="sp-stat-content">
            <p className="sp-stat-label">Academic Year</p>
            <h3 className="sp-stat-value">{performanceData[0]?.academicYear}</h3>
          </div>
        </div>
      </div>

      <div className="sp-details-card">
        <div className="sp-card-header">
          <h2 className="sp-card-title">Exam Performance</h2>
        </div>
        <div className="sp-card-content">
          <div className="sp-tabs">
            <div className="sp-tabs-list">
                {performanceData.map((assessment, index) => {
                  const eventId = assessment.eventId || assessment.assessmentId || assessment.examId;
                  const displayName = eventNameMap[eventId] || 
                                    assessment.eventName || 
                                    assessment.assessmentName || 
                                    (assessment.assessmentId && assessment.assessmentId.length < 30 ? assessment.assessmentId : null) ||
                                    (eventNameMap[eventId] === undefined ? 'Resolving Name...' : (eventNameMap[eventId] || (eventId === 'string' ? `Exam ${index + 1}` : 'Untitled Exam')));
                  
                  return (
                    <button 
                      key={assessment.studentPerformanceId || index} 
                      className={`sp-tab-button ${activeTab === (assessment.studentPerformanceId || 'tab-' + index) ? 'sp-active' : ''}`}
                      onClick={() => setActiveTab(assessment.studentPerformanceId || 'tab-' + index)}
                    >
                      {displayName}
                    </button>
                  );
                })}
              
                {/* Overall Marks tab removed as per request */}
            </div>
            
            <div className="sp-tab-content">
              {performanceData.map((assessment) => (
                <div 
                  key={assessment.studentPerformanceId} 
                  className={`sp-tab-panel ${activeTab === assessment.studentPerformanceId ? 'sp-active' : ''}`}
                >
                  {editMode && editData?.studentPerformanceId === assessment.studentPerformanceId ? (
                    <>
                      <table className="sp-performance-table">
                        <thead>
                          <tr>
                            <th>Course</th>
                            <th>Marks</th>
                            <th>Grade</th>
                          </tr>
                        </thead>
                        <tbody>
                          {editData.assessmentPerformanceList.map((course, index) => (
                            <tr key={course.courseId}>
                              <td>{courseNameMap[course.courseId] || 'Loading...'}</td>
                              <td>
                                <input
                                  type="number"
                                  value={course.marksObtained}
                                  onChange={(e) => handleInputChange(index, 'marksObtained', e.target.value)}
                                  max={course.maxMarks}
                                  min="0"
                                />
                                / {course.maxMarks}
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={course.gradeObtained}
                                  onChange={(e) => handleInputChange(index, 'gradeObtained', e.target.value)}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {editData.remarks && (
                        <div className="sp-remarks-section">
                          <p className="sp-remarks-label">Remarks</p>
                          <textarea
                            value={editData.remarks}
                            onChange={(e) => handleRemarksChange(e.target.value)}
                            className="sp-remarks-textarea"
                          />
                        </div>
                      )}
                      <div className="sp-edit-actions">
                        <button onClick={handleSave} className="sp-save-button">Save</button>
                        <button onClick={handleCancel} className="sp-cancel-button">Cancel</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <table className="sp-performance-table">
                        <thead>
                          <tr>
                            <th>Course</th>
                            <th>Marks</th>
                            <th>Grade</th>
                          </tr>
                        </thead>
                        <tbody>
                          {assessment?.assessmentPerformanceList?.map((course) => (
                            <tr key={course.courseId}>
                              <td>{courseNameMap[course.courseId] || 'Loading...'}</td>
                              <td>{course.marksObtained} / {course.maxMarks}</td>
                              <td>
                                <span className={`sp-grade-badge ${getGradeClass(course.gradeObtained || 'F')}`}>
                                  {course.gradeObtained || 'N/A'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {assessment.remarks && (
                        <div className="sp-remarks-section">
                          <p className="sp-remarks-label">Remarks</p>
                        <p className="sp-remarks-text">{assessment.remarks}</p>
                        </div>
                      )}
                      {/* <button 
                        onClick={() => handleEdit(assessment)}
                        className="sp-edit-button"
                      >
                        Edit
                      </button> */}
                    </>
                  )}
                </div>
              ))}

              {/* Overall Marks panel removed as per request */}
            </div>
          </div>
        </div>
      </div>
    </>
    )}
    </div>
  );
};

export default StudentPerformanceDashboard;